import { useMemo, useRef, useState, useEffect, useId, useCallback, ReactNode } from 'react';
import { default as dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import styles from './contentSamplingPlayer.module.scss';
import cn from 'classnames';
import { IconSkipBackward } from '@/assets/icons/IconSkipBackwards';
import { IconPlayButton } from '@/assets/icons/IconPlayButton';
import { IconPauseButton } from '@/assets/icons/IconPauseButton';
import { IconTranscript } from '@/assets/icons/IconTranscript';
import { IconSkipForward } from '@/assets/icons/IconSkipForward';
import { IconReplayButton } from '@/assets/icons/IconReplayButton';
import { Modal } from '../Modal/Modal';
import { Button } from '@/ui-components/atoms/Button/Button';

export interface SamplingModalProps {
    modalBody?: ReactNode;
}

export interface AudioPlayerProps {
    source: string;
    name?: string;
    isActive: boolean;
    onActivate: (isActive: boolean) => void;
    isContentSampler?: boolean;
    modal?: SamplingModalProps;
    isLightTheme?: boolean;
}

interface TimeState {
    currentTime: number;
    timeRemaining: number;
}

dayjs.extend(utc);

export function ContentSamplingPlayer({
    source,
    name = '',
    isActive,
    onActivate,
    modal,
    isLightTheme = false,
}: AudioPlayerProps): React.JSX.Element {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isEnded, setIsEnded] = useState<boolean>(false);
    const [isForward, setIsForward] = useState<boolean>(false);
    const [isBack, setIsBack] = useState<boolean>(false);
    const [duration, setDuration] = useState(0);
    const [progressPercent, setProgressPercent] = useState(0);
    const [time, setTime] = useState<TimeState>({
        currentTime: 0,
        timeRemaining: 0,
    });
    const [isModalOpen, setModelOpen] = useState(false);
    const showPauseButton = useMemo(() => !isEnded && isPlaying, [isPlaying, isEnded]);
    const showPlayButton = useMemo(() => !isEnded && !isPlaying, [isPlaying, isEnded]);

    const inputId = useId();

    const analytics: DataLayerInfo = {
        audioInfo: {
            name: name || source.split('/').pop() || '',
        },
    };

    const formatTime = (time: number): string => {
        return dayjs.utc(time * 1000).format('m:ss');
    };

    const handleAudioEnded = () => {
        setIsEnded(true);
        setIsPlaying(false);
    };

    const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audioElement = audioRef.current;
        const nextTime = Number(e.target.value);

        setTime({
            currentTime: nextTime,
            timeRemaining: Math.max((duration || 0) - nextTime, 0),
        });

        if (audioElement) {
            audioElement.currentTime = nextTime;
        }
    };

    const skipTrack = (direction: string) => {
        const audioElement = audioRef.current;

        if (direction === 'forward') {
            if (audioElement) {
                audioElement.currentTime = audioElement.currentTime + 15;
            }
            setIsForward(true);
        }
        if (direction === 'back') {
            if (audioElement) {
                audioElement.currentTime = audioElement.currentTime - 15;
            }
            setIsBack(true);
        }
    };

    const handleLoadedMetadata = useCallback(() => {
        const audioElement = audioRef.current;
        const nextDuration = audioElement?.duration || 0;

        setDuration(nextDuration);
        setTime((prev) => ({
            ...prev,
            timeRemaining: Math.max(nextDuration - prev.currentTime, 0),
        }));
    }, []);

    const handleTimeUpdate = () => {
        const audioElement = audioRef.current;
        const currentTime = audioElement?.currentTime ?? 0;

        const safeDuration = duration || 0;
        const progress = safeDuration > 0 ? Math.floor((currentTime / safeDuration) * 10000) / 100 : 0;

        setTime({
            currentTime,
            timeRemaining: Math.max(safeDuration - currentTime, 0),
        });
        setProgressPercent(progress);
    };

    const handlePlayPause = () => {
        const currentPlayingState = isPlaying;
        const newPlayingState = !currentPlayingState;

        onActivate(newPlayingState);
        setIsPlaying(newPlayingState);

        if (newPlayingState) {
            setIsEnded(false);
        }
    };

    useEffect(() => {
        if (audioRef.current?.readyState && audioRef.current?.readyState >= 1) {
            // If metadata is cached
            handleLoadedMetadata();
        }
    }, [handleLoadedMetadata]);

    useEffect(() => {
        const audioElement = audioRef?.current;

        if (isActive && isPlaying) {
            audioElement?.play();
        } else {
            audioElement?.pause();
            setIsPlaying(false);
        }
    }, [isPlaying, isActive]);

    useEffect(() => {
        if (isPlaying) {
            const userPlayEvent = new CustomEvent('sxm-media-play');
            audioRef.current?.dispatchEvent(userPlayEvent);
        } else if (isEnded) {
            const userCompleteEvent = new CustomEvent('sxm-media-complete');
            audioRef.current?.dispatchEvent(userCompleteEvent);
        } else {
            const userPauseEvent = new CustomEvent('sxm-media-pause');
            audioRef.current?.dispatchEvent(userPauseEvent);
        }
    }, [isPlaying, isEnded]);

    useEffect(() => {
        if (isForward) {
            const userForwardEvent = new CustomEvent('sxm-media-forward');
            audioRef.current?.dispatchEvent(userForwardEvent);
            setIsForward(false);
        } else if (isBack) {
            const userBackEvent = new CustomEvent('sxm-media-backward');
            audioRef.current?.dispatchEvent(userBackEvent);
            setIsBack(false);
        }
    }, [isForward, isBack]);

    return (
        <div className={styles.audioContainer}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Captions require a CMS-provided VTT file; this component provides a transcript modal as an alternative. */}
            <audio
                src={source}
                ref={audioRef}
                onEnded={handleAudioEnded}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                data-audioplayer={analytics.audioInfo.name}
            />
            <div className={cn(styles.controls, { [styles.active]: isActive })}>
                <div className={styles.slideAnimation}>
                    <div className={isLightTheme ? styles.lightProgressBarWrapper : styles.progressBarWrapper}>
                        <input
                            type="range"
                            id={inputId}
                            min={0}
                            max={duration || 0}
                            step="any"
                            value={time.currentTime ?? 0}
                            onChange={handleScrub}
                            aria-valuetext="Audio bar slider"
                            aria-label="Audio bar slider"
                        />
                        <div
                            className={styles.progressBar}
                            style={{
                                background: `linear-gradient(to right, var(--range-progress-bar-ellapsed) ${progressPercent}%, var(--range-progress-bar-remaining) ${progressPercent}%)`,
                            }}
                        />
                    </div>
                    <div className={styles.timeDisplayWrapper}>
                        <time
                            className={styles.timeEllapsed}
                            aria-label={`time ellapsed ${formatTime(time.currentTime)}`}
                        >
                            {formatTime(time.currentTime)}
                        </time>
                        <time
                            className={styles.timeRemaining}
                            aria-label={`time remaining ${formatTime(time.timeRemaining)}`}
                        >
                            {`-${formatTime(time.timeRemaining)}`}
                        </time>
                    </div>
                </div>
                <div className={isLightTheme ? styles.lightThemePlayerControls : styles.controlsWrapperStyles}>
                    <button onClick={() => skipTrack('back')} aria-label={`skip backward ${name}`} data-donottrack>
                        <IconSkipBackward fill={isLightTheme ? 'black' : 'white'} />
                    </button>
                    {showPlayButton && (
                        <button
                            onClick={handlePlayPause}
                            aria-label={`play ${name}`}
                            className={styles.playButton}
                            data-donottrack
                        >
                            <IconPlayButton fill={isLightTheme ? 'white' : 'black'} />
                        </button>
                    )}
                    {showPauseButton && (
                        <button
                            onClick={handlePlayPause}
                            aria-label={`pause ${name}`}
                            className={styles.pauseButton}
                            data-donottrack
                        >
                            <IconPauseButton fill={isLightTheme ? 'white' : 'black'} />
                        </button>
                    )}
                    {isEnded && (
                        <button
                            onClick={handlePlayPause}
                            aria-label={`replay ${name}`}
                            className={styles.replayButton}
                            data-donottrack
                        >
                            <IconReplayButton fill={isLightTheme ? 'black' : 'white'} />
                        </button>
                    )}
                    <button onClick={() => skipTrack('forward')} aria-label={`skip forward ${name}`} data-donottrack>
                        <IconSkipForward fill={isLightTheme ? 'black' : 'white'} />
                    </button>
                    {modal && (
                        <button
                            onClick={() => setModelOpen(true)}
                            aria-label={`open transcript ${name}`}
                            className={styles.transcriptIcon}
                            data-donottrack
                        >
                            <IconTranscript fill={isLightTheme ? 'black' : 'white'} />
                        </button>
                    )}
                </div>
            </div>
            {modal && (
                <Modal variant={'small'} open={isModalOpen} onClose={() => setModelOpen(false)} centeredLayout={true}>
                    {modal?.modalBody}
                    <Button
                        className={styles.closeButton}
                        variant={'secondary'}
                        type="button"
                        size="large"
                        ariaLabel={'Close'}
                        onClick={() => setModelOpen(false)}
                    >
                        {'Close'}
                    </Button>
                </Modal>
            )}
        </div>
    );
}
